import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {updateOwnersGlobalSetting} from '../../../UserPages/accounts/Overview/OverviewSlice/UserDetailsSlice'
import ToggleSwitch from 'react-bootstrap/Form'
import {ProfileDetailData} from '../../../../Redux/AdminPanel/ProfileDetailsSlice'
import {
  useActivateSubClientMutation,
  useDeactivateSubClientMutation,
} from '../../../../../services/cellcast.v2'
import {
  useLazyGetDnGracePeriodSettingQuery,
  useUpdateDnGracePeriodSettingMutation,
  useUpdateUserBillingDNTypeMutation,
  useLazyGetCustomOptOutQuery,
  useUpdateCustomOptOutMutation,
  useLazyGetDedicatedNumberCustomPriceQuery,
  useUpdateDedicatedNumberCustomPriceMutation,
  useDeleteDedicatedNumberCustomPriceMutation,
  useMigrateGatewayToConversationsMutation,
} from '../../../../../services/cellcast'
import {toast} from 'react-toastify'
import {globalSettingsTypes} from '../../../../Utils/Constant/status'
import {useLocation} from 'react-router-dom'
import {Accordion, Modal} from 'react-bootstrap'

// Reusable toggle row component
const SettingToggleRow = ({
  label,
  checked,
  onChange,
  id,
}: {
  label: string
  checked: boolean
  onChange: () => void
  id: string
}) => (
  <div className='d-flex justify-content-between align-items-center py-3 border-bottom'>
    <span className='text-gray-800 fw-semibold fs-6'>{label}</span>
    <ToggleSwitch.Check
      onChange={onChange}
      checked={checked}
      type='switch'
      id={id}
      className='form-check-solid'
    />
  </div>
)

const Settings = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const {userProp} = location.state || ({} as any)

  const userData = useSelector((state: any) => state?.ProfileData?.ProfileData?.user)
  const profileDetailState = userProp?.user ? userProp.user : userData

  // Global Settings state
  const [globalSettings, setGlobalSettings] = useState<string[]>([])
  const [fraudKeywords, setFraudKeywords] = useState('')
  const [savingGlobalSettings, setSavingGlobalSettings] = useState(false)

  // Accordion expanded state
  const [activeAccordionKeys, setActiveAccordionKeys] = useState<string[]>([
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
  ])

  // Sub Client state
  const [isSubClient, setIsSubClient] = useState(false)
  const [savingSubClient, setSavingSubClient] = useState(false)

  // DN Grace Period state
  const [dnGracePeriodStatus, setDnGracePeriodStatus] = useState(false)
  const [savingDnGracePeriod, setSavingDnGracePeriod] = useState(false)

  // Billing DN Type state
  const [billingDNType, setBillingDNType] = useState<'' | 'postpaid' | 'prepaid'>('')
  const [savingBillingDNType, setSavingBillingDNType] = useState(false)

  // Custom Opt-Out Page Branding state
  const [customLogo, setCustomLogo] = useState('')
  const [customLogoFile, setCustomLogoFile] = useState<File | null>(null)
  const [customLogoPreview, setCustomLogoPreview] = useState('')
  const [customTitle, setCustomTitle] = useState('')
  const [customFooter, setCustomFooter] = useState('')
  const [savingCustomOptOut, setSavingCustomOptOut] = useState(false)

  // Dedicated Number Custom Price state
  const [dnCustomPriceStatus, setDnCustomPriceStatus] = useState<'active' | 'deactive'>('deactive')
  const [dnCustomPriceMonthly, setDnCustomPriceMonthly] = useState<string>('')
  const [dnCustomPriceYearly, setDnCustomPriceYearly] = useState<string>('')
  const [savingDnCustomPrice, setSavingDnCustomPrice] = useState(false)
  const [hasDnCustomPrice, setHasDnCustomPrice] = useState(false)

  // Gateway to Conversations Migration state
  const [showMigrationModal, setShowMigrationModal] = useState(false)
  const [migrating, setMigrating] = useState(false)
  const [migrateGatewayToConversations] = useMigrateGatewayToConversationsMutation()

  // Mutations
  const [activateSubClient] = useActivateSubClientMutation()
  const [deactivateSubClient] = useDeactivateSubClientMutation()
  const [getDnGracePeriodSetting] = useLazyGetDnGracePeriodSettingQuery()
  const [updateDnGracePeriodSetting] = useUpdateDnGracePeriodSettingMutation()
  const [updateUserBillingDNType] = useUpdateUserBillingDNTypeMutation()
  const [getCustomOptOut] = useLazyGetCustomOptOutQuery()
  const [updateCustomOptOut] = useUpdateCustomOptOutMutation()
  const [getDedicatedNumberCustomPrice] = useLazyGetDedicatedNumberCustomPriceQuery()
  const [updateDedicatedNumberCustomPrice] = useUpdateDedicatedNumberCustomPriceMutation()
  const [deleteDedicatedNumberCustomPrice] = useDeleteDedicatedNumberCustomPriceMutation()

  useEffect(() => {
    if (!!profileDetailState?.globalSetting) {
      setGlobalSettings(profileDetailState?.globalSetting ?? [])
      setFraudKeywords(profileDetailState?.fraudKeywords ?? '')
    }

    if (profileDetailState && profileDetailState.isSubClientAccount) {
      setIsSubClient(profileDetailState.isSubClientAccount)
    } else {
      setIsSubClient(false)
    }

    // Initialize billing DN type
    if (profileDetailState?.billingDedicatedNumberType) {
      setBillingDNType(profileDetailState.billingDedicatedNumberType)
    } else {
      setBillingDNType('')
    }

    // Fetch DN Grace Period setting
    if (profileDetailState?._id) {
      getDnGracePeriodSetting({userId: profileDetailState._id})
        .unwrap()
        .then((res) => {
          if (res.data) {
            setDnGracePeriodStatus(res.data.status)
          }
        })
        .catch((err) => {
          console.error('Failed to fetch DN Grace Period setting:', err)
        })

      // Fetch Custom Opt-Out settings
      getCustomOptOut({userId: profileDetailState._id})
        .unwrap()
        .then((res) => {
          if (res.data) {
            setCustomLogo(res.data.customLogo || '')
            setCustomLogoPreview(res.data.customLogo || '')
            setCustomTitle(res.data.customTitle || '')
            setCustomFooter(res.data.customFooter || '')
          }
        })
        .catch((err) => {
          console.error('Failed to fetch Custom Opt-Out settings:', err)
        })

      // Fetch Dedicated Number Custom Price settings
      getDedicatedNumberCustomPrice({userId: profileDetailState._id})
        .unwrap()
        .then((res) => {
          if (res.data?.dedicatedNumberCustomPrice) {
            setHasDnCustomPrice(true)
            setDnCustomPriceStatus(res.data.dedicatedNumberCustomPrice.status || 'deactive')
            setDnCustomPriceMonthly(res.data.dedicatedNumberCustomPrice.monthly?.toString() || '')
            setDnCustomPriceYearly(res.data.dedicatedNumberCustomPrice.yearly?.toString() || '')
          } else {
            setHasDnCustomPrice(false)
            setDnCustomPriceStatus('deactive')
            setDnCustomPriceMonthly('')
            setDnCustomPriceYearly('')
          }
        })
        .catch((err) => {
          console.error('Failed to fetch Dedicated Number Custom Price:', err)
        })
    }
  }, [profileDetailState, getDnGracePeriodSetting, getCustomOptOut, getDedicatedNumberCustomPrice])

  const addOrRemoveFromArray = (arr: string[], value: string): string[] => {
    let tempData = [...arr]
    if (tempData.includes(value)) {
      tempData = tempData.filter((item) => item !== value)
    } else {
      tempData.push(value)
    }
    return tempData
  }

  // Handler for Global Settings
  const handleSaveGlobalSettings = async () => {
    setSavingGlobalSettings(true)
    try {
      await dispatch(
        updateOwnersGlobalSetting({
          globalSetting: globalSettings,
          fraudKeywords: fraudKeywords,
          user: profileDetailState?._id,
        })
      )
      await dispatch(ProfileDetailData(profileDetailState?._id))
      toast.success('Global settings updated successfully')
    } catch (err) {
      toast.error('Failed to update global settings')
    } finally {
      setSavingGlobalSettings(false)
    }
  }

  // Handler for Sub Client
  const handleSaveSubClient = async () => {
    setSavingSubClient(true)
    try {
      if (isSubClient === true) {
        const res = await activateSubClient({userId: profileDetailState?._id})
        if ('data' in res) {
          toast.success(res.data?.message || 'Sub client activated successfully')
        } else if ('error' in res) {
          toast.error('Failed to activate sub client')
        }
      } else {
        const res = await deactivateSubClient({userId: profileDetailState?._id})
        if ('data' in res) {
          toast.success(res.data?.message || 'Sub client deactivated successfully')
        } else if ('error' in res) {
          toast.error('Failed to deactivate sub client')
        }
      }
    } catch (err) {
      toast.error('Failed to update sub client setting')
    } finally {
      setSavingSubClient(false)
    }
  }

  // Handler for DN Grace Period
  const handleSaveDnGracePeriod = async () => {
    if (!profileDetailState?._id) return
    setSavingDnGracePeriod(true)
    try {
      const res = await updateDnGracePeriodSetting({
        userId: profileDetailState._id,
        data: {
          status: dnGracePeriodStatus,
          monthly: 3,
          yearly: 7,
        },
      })
      if ('data' in res) {
        toast.success('DN Grace Period setting updated successfully')
      } else if ('error' in res) {
        toast.error('Failed to update DN Grace Period setting')
      }
    } catch (err) {
      toast.error('Failed to update DN Grace Period setting')
    } finally {
      setSavingDnGracePeriod(false)
    }
  }

  // Handler for Billing DN Type
  const handleSaveBillingDNType = async () => {
    if (!profileDetailState?._id) return
    setSavingBillingDNType(true)
    try {
      const res = await updateUserBillingDNType({
        userId: profileDetailState._id,
        billingDedicatedNumberType: billingDNType,
      })
      if ('data' in res) {
        toast.success('Billing DN Type updated successfully')
      } else if ('error' in res) {
        toast.error('Failed to update Billing DN Type')
      }
    } catch (err) {
      toast.error('Failed to update Billing DN Type')
    } finally {
      setSavingBillingDNType(false)
    }
  }

  // Handler for Custom Opt-Out Logo file change
  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setCustomLogoFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setCustomLogoPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  // Handler for removing logo
  const handleRemoveLogo = () => {
    setCustomLogoFile(null)
    setCustomLogoPreview('')
    setCustomLogo('')
  }

  // Handler for Custom Opt-Out Page Branding
  const handleSaveCustomOptOut = async () => {
    if (!profileDetailState?._id) return
    setSavingCustomOptOut(true)
    try {
      const formData = new FormData()
      if (customLogoFile) {
        formData.append('file', customLogoFile)
      }
      formData.append('customTitle', customTitle)
      formData.append('customFooter', customFooter)

      const res = await updateCustomOptOut({
        userId: profileDetailState._id,
        formData,
      })
      if ('data' in res) {
        toast.success('Custom Opt-Out Page Branding updated successfully')
        if (res.data?.data) {
          setCustomLogo(res.data.data.customLogo || '')
          setCustomLogoPreview(res.data.data.customLogo || '')
          setCustomLogoFile(null)
        }
      } else if ('error' in res) {
        toast.error('Failed to update Custom Opt-Out Page Branding')
      }
    } catch (err) {
      toast.error('Failed to update Custom Opt-Out Page Branding')
    } finally {
      setSavingCustomOptOut(false)
    }
  }

  // Handler for Dedicated Number Custom Price
  const handleSaveDnCustomPrice = async () => {
    if (!profileDetailState?._id) return

    // Validate that prices are provided when status is deactive
    if (dnCustomPriceStatus === 'deactive') {
      if (!dnCustomPriceMonthly || !dnCustomPriceYearly) {
        toast.error('Monthly and yearly prices are required when custom pricing is enabled')
        return
      }
    }

    setSavingDnCustomPrice(true)
    try {
      const data: {status: 'active' | 'deactive'; monthly?: number; yearly?: number} = {
        status: dnCustomPriceStatus,
      }

      if (dnCustomPriceMonthly) {
        data.monthly = parseFloat(dnCustomPriceMonthly)
      }
      if (dnCustomPriceYearly) {
        data.yearly = parseFloat(dnCustomPriceYearly)
      }

      const res = await updateDedicatedNumberCustomPrice({
        userId: profileDetailState._id,
        data,
      })
      if ('data' in res) {
        toast.success('Dedicated Number Custom Price updated successfully')
        setHasDnCustomPrice(true)
      } else if ('error' in res) {
        const errorData = res.error as any
        toast.error(
          errorData?.data?.error?.message || 'Failed to update Dedicated Number Custom Price'
        )
      }
    } catch (err) {
      toast.error('Failed to update Dedicated Number Custom Price')
    } finally {
      setSavingDnCustomPrice(false)
    }
  }

  // Handler for deleting Dedicated Number Custom Price
  const handleDeleteDnCustomPrice = async () => {
    if (!profileDetailState?._id) return

    setSavingDnCustomPrice(true)
    try {
      const res = await deleteDedicatedNumberCustomPrice({
        userId: profileDetailState._id,
      })
      if ('data' in res) {
        toast.success('Dedicated Number Custom Price removed successfully')
        setHasDnCustomPrice(false)
        setDnCustomPriceStatus('deactive')
        setDnCustomPriceMonthly('')
        setDnCustomPriceYearly('')
      } else if ('error' in res) {
        toast.error('Failed to remove Dedicated Number Custom Price')
      }
    } catch (err) {
      toast.error('Failed to remove Dedicated Number Custom Price')
    } finally {
      setSavingDnCustomPrice(false)
    }
  }

  // Gateway to Conversations Migration
  const handleMigrateGatewayToConversations = async () => {
    if (!profileDetailState?._id) return

    setMigrating(true)
    const now = new Date()
    const dateTo = now.toISOString()
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const dateFrom = lastMonth.toISOString()

    try {
      const res = await migrateGatewayToConversations({
        userIds: [profileDetailState._id],
        dateFrom,
        dateTo,
      })
      if ('data' in res) {
        toast.success(res.data?.message || 'Migration initiated successfully')
        setShowMigrationModal(false)
      } else if ('error' in res) {
        const errorData = res.error as any
        toast.error(
          errorData?.data?.error?.message || 'Failed to initiate migration'
        )
      }
    } catch (err) {
      toast.error('Failed to initiate migration')
    } finally {
      setMigrating(false)
    }
  }

  return (
    <div className='col-md-12'>
      {/* Section 1: Global Settings */}
      <div className='card mb-6'>
        <div className='card-header'>
          <h3 className='card-title fw-bolder text-dark'>Global Settings</h3>
        </div>
        <div className='card-body p-0'>
          <Accordion
            alwaysOpen
            activeKey={activeAccordionKeys}
            onSelect={(keys) => setActiveAccordionKeys(keys as string[])}
          >
            {/* Reply Stop Optout Settings */}
            <Accordion.Item eventKey='0' className='border-0'>
              <Accordion.Header className='bg-light-primary'>
                <div className='d-flex align-items-center'>
                  <i className='bi bi-reply-fill fs-4 me-3 text-primary'></i>
                  <div>
                    <h5 className='mb-0 fw-bold text-gray-800'>Reply Stop Optout</h5>
                    <small className='text-muted'>Configure auto opt-out on STOP replies</small>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body className='px-6'>
                <div className='mb-4'>
                  <h6 className='text-gray-600 fw-bold mb-3'>Bulk Messages</h6>
                  <SettingToggleRow
                    label='SMS Reply Stop Optout'
                    checked={globalSettings?.includes(
                      globalSettingsTypes.BULK_SMS_REPLY_STOP_OUTPUT
                    )}
                    onChange={() =>
                      setGlobalSettings([
                        ...addOrRemoveFromArray(
                          globalSettings,
                          globalSettingsTypes.BULK_SMS_REPLY_STOP_OUTPUT
                        ),
                      ])
                    }
                    id='bulk-sms-switch'
                  />
                  <SettingToggleRow
                    label='MMS Reply Stop Optout'
                    checked={globalSettings?.includes(
                      globalSettingsTypes.BULK_MMS_REPLY_STOP_OUTPUT
                    )}
                    onChange={() =>
                      setGlobalSettings([
                        ...addOrRemoveFromArray(
                          globalSettings,
                          globalSettingsTypes.BULK_MMS_REPLY_STOP_OUTPUT
                        ),
                      ])
                    }
                    id='bulk-mms-switch'
                  />
                </div>

                <div className='mb-4'>
                  <h6 className='text-gray-600 fw-bold mb-3'>Quick Messages</h6>
                  <SettingToggleRow
                    label='SMS Reply Stop Optout'
                    checked={globalSettings?.includes(
                      globalSettingsTypes.QUICK_SMS_REPLY_STOP_OUTPUT
                    )}
                    onChange={() =>
                      setGlobalSettings([
                        ...addOrRemoveFromArray(
                          globalSettings,
                          globalSettingsTypes.QUICK_SMS_REPLY_STOP_OUTPUT
                        ),
                      ])
                    }
                    id='quick-sms-switch'
                  />
                  <SettingToggleRow
                    label='MMS Reply Stop Optout'
                    checked={globalSettings?.includes(
                      globalSettingsTypes.QUICK_MMS_REPLY_STOP_OUTPUT
                    )}
                    onChange={() =>
                      setGlobalSettings([
                        ...addOrRemoveFromArray(
                          globalSettings,
                          globalSettingsTypes.QUICK_MMS_REPLY_STOP_OUTPUT
                        ),
                      ])
                    }
                    id='quick-mms-switch'
                  />
                </div>

                <div className='mb-4'>
                  <h6 className='text-gray-600 fw-bold mb-3'>API Messages</h6>
                  <SettingToggleRow
                    label='SMS Reply Stop Optout'
                    checked={globalSettings?.includes(
                      globalSettingsTypes.API_SMS_REPLY_STOP_OUTPUT
                    )}
                    onChange={() =>
                      setGlobalSettings([
                        ...addOrRemoveFromArray(
                          globalSettings,
                          globalSettingsTypes.API_SMS_REPLY_STOP_OUTPUT
                        ),
                      ])
                    }
                    id='api-sms-switch'
                  />
                  <SettingToggleRow
                    label='MMS Reply Stop Optout'
                    checked={globalSettings?.includes(
                      globalSettingsTypes.API_MMS_REPLY_STOP_OUTPUT
                    )}
                    onChange={() =>
                      setGlobalSettings([
                        ...addOrRemoveFromArray(
                          globalSettings,
                          globalSettingsTypes.API_MMS_REPLY_STOP_OUTPUT
                        ),
                      ])
                    }
                    id='api-mms-switch'
                  />
                </div>

                <div>
                  <h6 className='text-gray-600 fw-bold mb-3'>Email to SMS</h6>
                  <SettingToggleRow
                    label='Email to SMS Reply Stop Optout'
                    checked={globalSettings?.includes(
                      globalSettingsTypes.EMAIL_TO_SMS_REPLY_STOP_OUTPUT
                    )}
                    onChange={() =>
                      setGlobalSettings([
                        ...addOrRemoveFromArray(
                          globalSettings,
                          globalSettingsTypes.EMAIL_TO_SMS_REPLY_STOP_OUTPUT
                        ),
                      ])
                    }
                    id='email-sms-switch'
                  />
                </div>
              </Accordion.Body>
            </Accordion.Item>

            {/* Message Settings */}
            <Accordion.Item eventKey='1' className='border-0 border-top'>
              <Accordion.Header className='bg-light-warning'>
                <div className='d-flex align-items-center'>
                  <i className='bi bi-chat-dots-fill fs-4 me-3 text-warning'></i>
                  <div>
                    <h5 className='mb-0 fw-bold text-gray-800'>Message Settings</h5>
                    <small className='text-muted'>
                      Configure messaging to unsubscribed contacts & shared DNs
                    </small>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body className='px-6'>
                <div className='mb-4'>
                  <h6 className='text-gray-600 fw-bold mb-3'>Message to Unsubscribed Contacts</h6>
                  <SettingToggleRow
                    label='Allow Bulk Messages'
                    checked={globalSettings?.includes(
                      globalSettingsTypes.BULK_MESSAGE_TO_UNSUB_CONTACT
                    )}
                    onChange={() =>
                      setGlobalSettings([
                        ...addOrRemoveFromArray(
                          globalSettings,
                          globalSettingsTypes.BULK_MESSAGE_TO_UNSUB_CONTACT
                        ),
                      ])
                    }
                    id='bulk-msg-unsub-switch'
                  />
                  <SettingToggleRow
                    label='Allow Quick Messages'
                    checked={globalSettings?.includes(
                      globalSettingsTypes.QUICK_MESSAGE_TO_UNSUB_CONTACT
                    )}
                    onChange={() =>
                      setGlobalSettings([
                        ...addOrRemoveFromArray(
                          globalSettings,
                          globalSettingsTypes.QUICK_MESSAGE_TO_UNSUB_CONTACT
                        ),
                      ])
                    }
                    id='quick-msg-unsub-switch'
                  />
                </div>

                <div>
                  <h6 className='text-gray-600 fw-bold mb-3'>Shared Dedicated Numbers</h6>
                  <SettingToggleRow
                    label='Enable Inbound to All Shared DN'
                    checked={globalSettings?.includes(
                      globalSettingsTypes.INBOUND_SHARE_TO_ALL_SHARED_DN
                    )}
                    onChange={() =>
                      setGlobalSettings([
                        ...addOrRemoveFromArray(
                          globalSettings,
                          globalSettingsTypes.INBOUND_SHARE_TO_ALL_SHARED_DN
                        ),
                      ])
                    }
                    id='inbound-shared-dn-switch'
                  />
                </div>
              </Accordion.Body>
            </Accordion.Item>

            {/* Sender & Pricing Settings */}
            <Accordion.Item eventKey='2' className='border-0 border-top'>
              <Accordion.Header className='bg-light-success'>
                <div className='d-flex align-items-center'>
                  <i className='bi bi-currency-dollar fs-4 me-3 text-success'></i>
                  <div>
                    <h5 className='mb-0 fw-bold text-gray-800'>Sender & Pricing</h5>
                    <small className='text-muted'>Configure sender IDs and SMS pricing</small>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body className='px-6'>
                <SettingToggleRow
                  label='Disable OTP verification for custom sender IDs'
                  checked={globalSettings?.includes(
                    globalSettingsTypes.SENDER_IDS_DISABLE_SEND_OTP
                  )}
                  onChange={() =>
                    setGlobalSettings([
                      ...addOrRemoveFromArray(
                        globalSettings,
                        globalSettingsTypes.SENDER_IDS_DISABLE_SEND_OTP
                      ),
                    ])
                  }
                  id='disable-otp-switch'
                />
                <SettingToggleRow
                  label='Apply 1.3x price multiplier for custom/dedicated numbers'
                  checked={globalSettings?.includes(globalSettingsTypes.SEND_WITH_CUSTOM_PRICE)}
                  onChange={() =>
                    setGlobalSettings([
                      ...addOrRemoveFromArray(
                        globalSettings,
                        globalSettingsTypes.SEND_WITH_CUSTOM_PRICE
                      ),
                    ])
                  }
                  id='custom-price-switch'
                />
              </Accordion.Body>
            </Accordion.Item>

            {/* Fraud Protection */}
            <Accordion.Item eventKey='3' className='border-0 border-top'>
              <Accordion.Header className='bg-light-danger'>
                <div className='d-flex align-items-center'>
                  <i className='bi bi-shield-exclamation fs-4 me-3 text-danger'></i>
                  <div>
                    <h5 className='mb-0 fw-bold text-gray-800'>Fraud Protection</h5>
                    <small className='text-muted'>Configure fraud keyword filtering</small>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body className='px-6'>
                <SettingToggleRow
                  label='Disable fraud keyword filter for all outgoing messages'
                  checked={globalSettings?.includes(globalSettingsTypes.DISABLE_FRAUD_KEYWORD)}
                  onChange={() =>
                    setGlobalSettings([
                      ...addOrRemoveFromArray(
                        globalSettings,
                        globalSettingsTypes.DISABLE_FRAUD_KEYWORD
                      ),
                    ])
                  }
                  id='disable-fraud-switch'
                />
                <SettingToggleRow
                  label='Enable custom fraud keywords'
                  checked={globalSettings?.includes(globalSettingsTypes.CUSTOM_FRAUD_KEYWORD)}
                  onChange={() =>
                    setGlobalSettings([
                      ...addOrRemoveFromArray(
                        globalSettings,
                        globalSettingsTypes.CUSTOM_FRAUD_KEYWORD
                      ),
                    ])
                  }
                  id='custom-fraud-switch'
                />
                {globalSettings?.includes(globalSettingsTypes.CUSTOM_FRAUD_KEYWORD) && (
                  <div className='mt-4'>
                    <label className='form-label fw-semibold text-gray-700'>
                      Custom Fraud Keywords
                    </label>
                    <textarea
                      placeholder='Enter fraud keywords (comma separated)'
                      className='form-control form-control-solid'
                      value={fraudKeywords}
                      rows={3}
                      onChange={(e) => setFraudKeywords(e.currentTarget.value)}
                    />
                    <small className='text-muted'>
                      Messages containing these keywords will be blocked
                    </small>
                  </div>
                )}
              </Accordion.Body>
            </Accordion.Item>

            {/* UI & Navigation */}
            <Accordion.Item eventKey='4' className='border-0 border-top'>
              <Accordion.Header className='bg-light-info'>
                <div className='d-flex align-items-center'>
                  <i className='bi bi-layout-sidebar fs-4 me-3 text-info'></i>
                  <div>
                    <h5 className='mb-0 fw-bold text-gray-800'>UI & Navigation</h5>
                    <small className='text-muted'>Configure interface preferences</small>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body className='px-6'>
                <SettingToggleRow
                  label='Enable New Navigation (V3)'
                  checked={globalSettings?.includes(globalSettingsTypes.NEW_NAVIGATION_V3)}
                  onChange={() =>
                    setGlobalSettings([
                      ...addOrRemoveFromArray(
                        globalSettings,
                        globalSettingsTypes.NEW_NAVIGATION_V3
                      ),
                    ])
                  }
                  id='new-nav-switch'
                />
                <SettingToggleRow
                  label='Enable New Inbox'
                  checked={globalSettings?.includes(globalSettingsTypes.NEW_INBOX)}
                  onChange={() =>
                    setGlobalSettings([
                      ...addOrRemoveFromArray(globalSettings, globalSettingsTypes.NEW_INBOX),
                    ])
                  }
                  id='new-inbox-switch'
                />
                {globalSettings?.includes(globalSettingsTypes.NEW_INBOX) && (
                  <div className='d-flex'>
                    <button
                      type='button'
                      className='btn btn-primary'
                      onClick={() => setShowMigrationModal(true)}
                      disabled={migrating}
                    >
                      {migrating ? (
                        <>
                          <span className='spinner-border spinner-border-sm me-2'></span>
                          Migrating...
                        </>
                      ) : (
                        <>
                          <i className='bi bi-arrow-right-circle me-2'></i>
                          Sync Conversations
                        </>
                      )}
                    </button>
                  </div>
                )}
              </Accordion.Body>
            </Accordion.Item>

            {/* Sub Client Auto Verify */}
            <Accordion.Item eventKey='5' className='border-0 border-top'>
              <Accordion.Header className='bg-light-secondary'>
                <div className='d-flex align-items-center'>
                  <i className='bi bi-person-check-fill fs-4 me-3 text-secondary'></i>
                  <div>
                    <h5 className='mb-0 fw-bold text-gray-800'>Sub Client Auto Verify</h5>
                    <small className='text-muted'>Configure sub client verification settings</small>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body className='px-6'>
                <SettingToggleRow
                  label='New Sub Client User Auto Verify'
                  checked={globalSettings?.includes(globalSettingsTypes.NEW_SUB_CLIENT_AUTO_VERIFY)}
                  onChange={() =>
                    setGlobalSettings([
                      ...addOrRemoveFromArray(
                        globalSettings,
                        globalSettingsTypes.NEW_SUB_CLIENT_AUTO_VERIFY
                      ),
                    ])
                  }
                  id='sub-client-auto-verify-switch'
                />
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>
        <div className='card-footer d-flex justify-content-end py-6 px-9'>
          <button
            onClick={handleSaveGlobalSettings}
            type='button'
            className='btn btn-primary'
            disabled={savingGlobalSettings}
          >
            {savingGlobalSettings ? (
              <>
                <span className='spinner-border spinner-border-sm me-2'></span>
                Saving...
              </>
            ) : (
              <>
                <i className='bi bi-check2 me-2'></i>
                Save Global Settings
              </>
            )}
          </button>
        </div>
      </div>

      {/* Section 2: Sub Client Settings */}
      <div className='card mb-6'>
        <div className='card-header'>
          <h3 className='card-title fw-bolder text-dark'>Sub Client Settings</h3>
        </div>
        <div className='card-body'>
          <div className='row globalSettingBox'>
            <div
              className='col-6 mt-2'
              style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'center',
              }}
            >
              <span
                style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                }}
              >
                Can Manage Sub Client User ?
              </span>
              <ToggleSwitch.Check
                onChange={() => setIsSubClient(!isSubClient)}
                checked={isSubClient}
                type='switch'
                id='sub-client-switch'
                className='col-4'
              />
            </div>
          </div>
        </div>
        <div className='card-footer d-flex justify-content-end py-6 px-9'>
          <button
            onClick={handleSaveSubClient}
            type='button'
            className='btn btn-sm btn-primary'
            disabled={savingSubClient}
          >
            {savingSubClient ? 'Saving...' : 'Save Sub Client Settings'}
          </button>
        </div>
      </div>

      {/* Section 3: DN Subscription Grace Period */}
      <div className='card mb-6'>
        <div className='card-header'>
          <h3 className='card-title fw-bolder text-dark'>DN Subscription Grace Period</h3>
        </div>
        <div className='card-body'>
          <div className='row globalSettingBox'>
            <div
              className='col-6 mt-2'
              style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'center',
              }}
            >
              <span
                style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                }}
              >
                Enable DN Subscription Grace Period
              </span>
              <ToggleSwitch.Check
                onChange={() => setDnGracePeriodStatus(!dnGracePeriodStatus)}
                checked={dnGracePeriodStatus}
                type='switch'
                id='dn-grace-period-switch'
                className='col-4'
              />
            </div>
          </div>
          <div className='row mt-2'>
            <div className='col-12'>
              <small className='text-muted'>
                When enabled, users will have a grace period before their dedicated number
                subscription is cancelled after a failed payment.
              </small>
            </div>
          </div>
        </div>
        <div className='card-footer d-flex justify-content-end py-6 px-9'>
          <button
            onClick={handleSaveDnGracePeriod}
            type='button'
            className='btn btn-sm btn-primary'
            disabled={savingDnGracePeriod}
          >
            {savingDnGracePeriod ? 'Saving...' : 'Save Grace Period Settings'}
          </button>
        </div>
      </div>

      {/* Section 4: Dedicated Number Billing Type */}
      <div className='card mb-6'>
        <div className='card-header'>
          <h3 className='card-title fw-bolder text-dark'>Dedicated Number Billing Type</h3>
        </div>
        <div className='card-body'>
          <div className='d-flex justify-content-between align-items-center py-3 border-bottom'>
            <span className='text-gray-800 fw-semibold fs-6'>Billing Type</span>
            <select
              className='form-select form-select-solid'
              style={{maxWidth: '200px'}}
              value={billingDNType}
              onChange={(e) => setBillingDNType(e.target.value as '' | 'postpaid' | 'prepaid')}
            >
              <option value=''>Default</option>
              <option value='prepaid'>Prepaid</option>
              <option value='postpaid'>Postpaid</option>
            </select>
          </div>
          <div className='row mt-3'>
            <div className='col-12'>
              <small className='text-muted'>
                <strong>Default:</strong> Standard billing behavior.
                <br />
                <strong>Prepaid:</strong> User pays upfront via credit card subscription for each
                dedicated number.
                <br />
                <strong>Postpaid:</strong> User will receive a consolidated invoice at the end of
                each month for all dedicated numbers. No credit card required at purchase.
              </small>
            </div>
          </div>
        </div>
        <div className='card-footer d-flex justify-content-end py-6 px-9'>
          <button
            onClick={handleSaveBillingDNType}
            type='button'
            className='btn btn-sm btn-primary'
            disabled={savingBillingDNType}
          >
            {savingBillingDNType ? 'Saving...' : 'Save Billing Type'}
          </button>
        </div>
      </div>

      {/* Section 5: Dedicated Number Custom Price */}
      <div className='card mb-6'>
        <div className='card-header'>
          <h3 className='card-title fw-bolder text-dark'>Dedicated Number Custom Price</h3>
        </div>
        <div className='card-body'>
          <div className='row mb-6'>
            <div className='col-12'>
              <small className='text-muted'>
                Set custom pricing for dedicated numbers for this user. When custom pricing is
                enabled (status: deactive), the user will be charged the specified monthly and
                yearly rates instead of the default rates.
              </small>
            </div>
          </div>

          {/* Status Toggle */}
          <div className='d-flex justify-content-between align-items-center py-3 border-bottom'>
            <div>
              <span className='text-gray-800 fw-semibold fs-6'>Custom Pricing Status</span>
              <div className='text-muted fs-7'>
                {dnCustomPriceStatus === 'deactive'
                  ? 'Custom pricing is enabled - user pays custom rates'
                  : 'Custom pricing is disabled - user pays default rates'}
              </div>
            </div>
            <select
              className='form-select form-select-solid'
              style={{maxWidth: '200px'}}
              value={dnCustomPriceStatus}
              onChange={(e) => setDnCustomPriceStatus(e.target.value as 'active' | 'deactive')}
            >
              <option value='active'>Active (Default Pricing)</option>
              <option value='deactive'>Deactive (Custom Pricing)</option>
            </select>
          </div>

          {/* Monthly Price */}
          <div className='row mb-4 mt-4'>
            <label className='col-lg-3 col-form-label fw-semibold fs-6'>
              Monthly Price ($)
              {dnCustomPriceStatus === 'deactive' && <span className='text-danger'>*</span>}
            </label>
            <div className='col-lg-9'>
              <input
                type='number'
                step='0.01'
                min='0'
                className='form-control form-control-solid'
                placeholder='Enter monthly price'
                value={dnCustomPriceMonthly}
                onChange={(e) => setDnCustomPriceMonthly(e.target.value)}
              />
            </div>
          </div>

          {/* Yearly Price */}
          <div className='row mb-4'>
            <label className='col-lg-3 col-form-label fw-semibold fs-6'>
              Yearly Price ($)
              {dnCustomPriceStatus === 'deactive' && <span className='text-danger'>*</span>}
            </label>
            <div className='col-lg-9'>
              <input
                type='number'
                step='0.01'
                min='0'
                className='form-control form-control-solid'
                placeholder='Enter yearly price'
                value={dnCustomPriceYearly}
                onChange={(e) => setDnCustomPriceYearly(e.target.value)}
              />
            </div>
          </div>

          {dnCustomPriceStatus === 'deactive' && (
            <div className='alert alert-warning d-flex align-items-center p-3'>
              <i className='bi bi-exclamation-triangle-fill fs-4 me-3 text-warning'></i>
              <div>
                <strong>Note:</strong> Both monthly and yearly prices are required when custom
                pricing is enabled.
              </div>
            </div>
          )}
        </div>
        <div className='card-footer d-flex justify-content-end gap-3 py-6 px-9'>
          {hasDnCustomPrice && (
            <button
              onClick={handleDeleteDnCustomPrice}
              type='button'
              className='btn btn-sm btn-light-danger'
              disabled={savingDnCustomPrice}
            >
              {savingDnCustomPrice ? 'Removing...' : 'Remove Custom Price'}
            </button>
          )}
          <button
            onClick={handleSaveDnCustomPrice}
            type='button'
            className='btn btn-sm btn-primary'
            disabled={savingDnCustomPrice}
          >
            {savingDnCustomPrice ? 'Saving...' : 'Save Custom Price'}
          </button>
        </div>
      </div>

      {/* Section 6: Custom Opt-Out Page Branding */}
      <div className='card mb-6'>
        <div className='card-header'>
          <h3 className='card-title fw-bolder text-dark'>Custom Opt-Out Page Branding</h3>
        </div>
        <div className='card-body'>
          <div className='row mb-6'>
            <div className='col-12'>
              <small className='text-muted'>
                Customize the opt-out page that users see when they unsubscribe. You can add a
                custom logo, title, and footer text.
              </small>
            </div>
          </div>

          {/* Logo Upload */}
          <div className='row mb-6'>
            <label className='col-lg-3 col-form-label fw-semibold fs-6'>Custom Logo</label>
            <div className='col-lg-9'>
              <div className='d-flex align-items-center'>
                {customLogoPreview ? (
                  <div className='position-relative me-4'>
                    <img
                      src={customLogoPreview}
                      alt='Custom Logo Preview'
                      style={{
                        maxWidth: '150px',
                        maxHeight: '80px',
                        objectFit: 'contain',
                        border: '1px solid #e4e6ef',
                        borderRadius: '6px',
                        padding: '8px',
                      }}
                    />
                    <button
                      type='button'
                      className='btn btn-icon btn-sm btn-light-danger position-absolute'
                      style={{top: '-8px', right: '-8px'}}
                      onClick={handleRemoveLogo}
                    >
                      <i className='bi bi-x fs-5'></i>
                    </button>
                  </div>
                ) : (
                  <div
                    className='d-flex align-items-center justify-content-center me-4'
                    style={{
                      width: '150px',
                      height: '80px',
                      border: '2px dashed #e4e6ef',
                      borderRadius: '6px',
                      backgroundColor: '#f9f9f9',
                    }}
                  >
                    <span className='text-muted fs-7'>No logo</span>
                  </div>
                )}
                <div>
                  <label className='btn btn-sm btn-light-primary' htmlFor='customLogoUpload'>
                    <i className='bi bi-upload me-2'></i>
                    {customLogoPreview ? 'Change Logo' : 'Upload Logo'}
                  </label>
                  <input
                    type='file'
                    id='customLogoUpload'
                    accept='image/*'
                    onChange={handleLogoFileChange}
                    style={{display: 'none'}}
                  />
                  <div className='text-muted fs-7 mt-2'>Recommended: PNG or JPG, max 2MB</div>
                </div>
              </div>
            </div>
          </div>

          {/* Custom Title */}
          <div className='row mb-6'>
            <label className='col-lg-3 col-form-label fw-semibold fs-6'>Custom Title</label>
            <div className='col-lg-9'>
              <input
                type='text'
                className='form-control form-control-solid'
                placeholder='Enter custom title for opt-out page'
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
              />
              <div className='text-muted fs-7 mt-2'>
                This title will be displayed at the top of the opt-out page
              </div>
            </div>
          </div>

          {/* Custom Footer */}
          <div className='row mb-4'>
            <label className='col-lg-3 col-form-label fw-semibold fs-6'>Custom Footer</label>
            <div className='col-lg-9'>
              <textarea
                className='form-control form-control-solid'
                placeholder='Enter custom footer text for opt-out page'
                rows={3}
                value={customFooter}
                onChange={(e) => setCustomFooter(e.target.value)}
              />
              <div className='text-muted fs-7 mt-2'>
                This text will be displayed at the bottom of the opt-out page
              </div>
            </div>
          </div>
        </div>
        <div className='card-footer d-flex justify-content-end py-6 px-9'>
          <button
            onClick={handleSaveCustomOptOut}
            type='button'
            className='btn btn-sm btn-primary'
            disabled={savingCustomOptOut}
          >
            {savingCustomOptOut ? (
              <>
                <span className='spinner-border spinner-border-sm me-2'></span>
                Saving...
              </>
            ) : (
              'Save Custom Opt-Out Branding'
            )}
          </button>
        </div>
      </div>

      {/* Gateway to Conversations Migration Modal */}
      <Modal
        show={showMigrationModal}
        onHide={() => !migrating && setShowMigrationModal(false)}
        backdrop='static'
        dialogClassName='modal-dialog-centered mw-450px'
      >
        <div className='modal-header'>
          <h3 className='modal-title'>Confirm Migration</h3>
          <button
            type='button'
            className='btn-close'
            onClick={() => !migrating && setShowMigrationModal(false)}
            disabled={migrating}
          ></button>
        </div>
        <div className='modal-body'>
          <p>
            Are you sure you want to sync message data to conversations for this user? This will
            process messages in the background from the last month.
          </p>
        </div>
        <div className='modal-footer'>
          <button
            type='button'
            className='btn btn-light'
            onClick={() => setShowMigrationModal(false)}
            disabled={migrating}
          >
            Cancel
          </button>
          <button
            type='button'
            className='btn btn-primary'
            onClick={handleMigrateGatewayToConversations}
            disabled={migrating}
          >
            {migrating ? (
              <>
                <span className='spinner-border spinner-border-sm me-2'></span>
                Migrating...
              </>
            ) : (
              'Confirm & Run'
            )}
          </button>
        </div>
      </Modal>
    </div>
  )
}

export default Settings
